import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { AgGridModule } from 'ag-grid-angular';
import { ColDef, GridApi, GridReadyEvent, CellClassParams, CellValueChangedEvent, GetRowIdParams, ITooltipParams } from 'ag-grid-community';
import { Subscription, firstValueFrom } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { RateManagerService } from 'src/app/services/modules/instant-rate-manager/rate-manager.service';
import { AlertUtil } from 'src/app/shared/utils/alert-dialog.util';
import { BulkUploadProgressService, BulkUploadState } from '../bulk-create-rate-modal/bulk-upload-progress.service';
import { BulkCreateRateModalComponent } from '../bulk-create-rate-modal/bulk-create-rate-modal.component';
import { LocationCellEditorComponent } from '../location-cell-editor/location-cell-editor.component';
import { DropdownCellEditorComponent } from '../dropdown-cell-editor/dropdown-cell-editor.component';
import { RatePreviewRow, PreviewFilter, PreviewSummary, CellError } from './bulk-upload-preview.types';

@Component({
  selector: 'app-bulk-upload-preview',
  standalone: true,
  imports: [
    CommonModule,
    AgGridModule,
  ],
  templateUrl: './bulk-upload-preview.component.html',
  styleUrl: './bulk-upload-preview.component.scss',
})
export class BulkUploadPreviewComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private rateManagerService = inject(RateManagerService);
  private uploadProgress = inject(BulkUploadProgressService);
  private modalService = inject(NgbModal);

  private gridApi!: GridApi;
  private stateSub?: Subscription;
  private queryParamSub?: Subscription;

  // Grid configuration
  columnDefs: ColDef[] = [];
  rowData: RatePreviewRow[] = [];
  allRows: RatePreviewRow[] = [];
  defaultColDef: ColDef = {
    minWidth: 150,
    resizable: true,
    editable: true,
    sortable: true,
    filter: true,
    cellClassRules: {
      'cell-edited': (params: any) => {
        if (!params.data) return false;
        const rowId = params.data._rowId;
        const entry = this.pendingEdits.get(rowId);
        return !!(entry && params.colDef.field && params.colDef.field in entry.changes);
      },
    },
    tooltipValueGetter: (params: ITooltipParams) => {
      const field = (params.colDef as ColDef)?.field;
      if (!field || !params.data) return '';
      const error = params.data._cellErrors?.[field];
      return error ? error.message : '';
    },
  };

  // State
  activeFilter: PreviewFilter = 'all';
  summary: PreviewSummary = {
    totalRows: 0,
    validRows: 0,
    errorRows: 0,
    warningRows: 0,
    excludedRows: 0,
    createdRates: 0,
    updatedRates: 0,
    skippedRoutes: 0,
  };
  selectedRowCount = 0;
  isSubmitting = false;
  commitFailed = false;
  isLoading = true;
  fileName = '';
  categoryId = 0;
  categoryName = '';
  /** The backend job ID — used for commit */
  jobId: string | null = null;
  /** True when the job stage is 'validation_failed' (e.g. wrong template for the category) */
  validationFailed = false;
  /** Message from the backend explaining the validation failure */
  validationFailedMessage = '';

  // Inline edit tracking (cell-level, like rate listing).
  // Keyed by the GLOBALLY-unique _rowId so edits survive page navigation.
  // `rowData` holds a full snapshot of the row's committable fields so the
  // commit payload can include edits made on pages that are no longer loaded.
  pendingEdits: Map<
    number,
    {
      changes: Record<string, any>;
      originalValues: Record<string, any>;
      rowData: Record<string, any>;
    }
  > = new Map();

  get editedRowCount(): number {
    return this.pendingEdits.size;
  }

  // ─── Pagination (server-side) ─────────────────────────────────────────────
  page = 1;
  pageSize = 50;
  totalRows = 0;
  /** True when the backend is serving fewer rows than the total (paged). */
  isPaginated = false;
  /** Error/warning totals reported by the backend across ALL pages. */
  private serverErrorCount = 0;
  private serverWarningCount = 0;

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalRows / (this.pageSize || 1)));
  }

  get pageStart(): number {
    return this.totalRows === 0 ? 0 : (this.page - 1) * this.pageSize + 1;
  }

  get pageEnd(): number {
    return Math.min(this.page * this.pageSize, this.totalRows);
  }

  get pageSizeOptions(): number[] {
    return [
      25,
      50,
      100,
      200,
      500,
      1000,
    ];
  }

  // From the upload state
  private previewResult: any = null;

  ngOnInit(): void {
    this.stateSub = this.uploadProgress.changes.subscribe((state) => {
      this.loadFromState(state);
    });

    // Subscribe to queryParams so switching jobs while already on this page triggers a reload
    this.queryParamSub = this.route.queryParamMap.subscribe((params) => {
      const jobId = params.get('jobId');
      if (jobId) {
        this.resetPreviewState();
        this.loadFromApi(jobId);
      } else {
        // Fallback: try to load from the progress service state
        const state = this.uploadProgress.snapshot;
        if (state.jobId) {
          this.loadFromState(state);
        } else {
          // No job to display — redirect back
          this.router.navigate([
            '/instant-rate-manager',
          ]);
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.stateSub?.unsubscribe();
    this.queryParamSub?.unsubscribe();
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
    if (this.isLoading) {
      this.gridApi.showLoadingOverlay();
    } else {
      this.autoSizeColumns();
    }
  }

  /**
   * Fires when AG Grid's local pagination changes (non-paginated mode).
   * When server-side pagination is active we suppress the native panel
   * and handle page navigation ourselves, so this is a no-op then.
   */
  onPaginationChanged(_event: any): void {
    // Only relevant for the non-paginated (single page) case where
    // AG Grid handles all rows locally — nothing extra needed.
  }

  private autoSizeColumns(): void {
    if (!this.gridApi) return;
    const columns = this.gridApi.getColumns();
    if (columns) {
      const allColumnIds = columns.map((col: any) => col.getColId());
      this.gridApi.autoSizeColumns(allColumnIds, false);
    }
  }

  getRowId = (params: GetRowIdParams): string => {
    return String(params.data._rowId);
  };

  getRowStyle = (params: any): any => {
    if (params.data && this.pendingEdits.has(params.data._rowId)) {
      return { background: '#FFF9C4' };
    }
    return undefined;
  };

  // ─── Data Loading ─────────────────────────────────────────────────────────────

  private loadFromState(state: BulkUploadState): void {
    if (state.completed && state.result && !this.previewResult) {
      this.previewResult = state.result;
      this.fileName = state.fileName;
      this.jobId = state.jobId ? String(state.jobId) : null;
      this.categoryId = state.result.rate_category_id || state.categoryId || 0;
      this.categoryName = state.result.category_display_name || state.categoryDisplayName || state.result.rate_category_name || state.categoryName || '';

      // Detect validation failure from the job data (error field present in failed responses)
      if (state.result.error) {
        this.validationFailed = true;
        this.validationFailedMessage = state.result.error || state.result.message || 'Validation failed. The uploaded file may not match the selected category template.';
      }
      this.buildPreviewData(state.result);

      this.isLoading = false;
      if (this.gridApi) {
        this.gridApi.hideOverlay();
        this.autoSizeColumns();
      }
    }
  }

  /**
   * Reset internal state so the component can load a different job cleanly.
   */
  private resetPreviewState(): void {
    this.previewResult = null;
    this.columnDefs = [];
    this.rowData = [];
    this.allRows = [];
    this.fileName = '';
    this.categoryId = 0;
    this.categoryName = '';
    this.jobId = null;
    this.isLoading = true;
    this.isSubmitting = false;
    this.commitFailed = false;
    this.validationFailed = false;
    this.validationFailedMessage = '';
    this.activeFilter = 'all';
    this.pendingEdits.clear();
    this.page = 1;
    this.pageSize = 50;
    this.totalRows = 0;
    this.isPaginated = false;
    this.serverErrorCount = 0;
    this.serverWarningCount = 0;
    this.summary = {
      totalRows: 0,
      validRows: 0,
      errorRows: 0,
      warningRows: 0,
      excludedRows: 0,
      createdRates: 0,
      updatedRates: 0,
      skippedRoutes: 0,
    };
  }

  /**
   * Load job data directly from the API using a jobId.
   * Used when navigating from the Jobs dropdown "Review" button.
   */
  private async loadFromApi(jobId: string): Promise<void> {
    this.jobId = jobId;
    this.isLoading = true;
    if (this.gridApi) {
      this.gridApi.showLoadingOverlay();
    }
    try {
      const response = await firstValueFrom(
        this.rateManagerService.getUploadJobStatus(jobId, {
          page: 1,
          page_size: this.pageSize,
        }),
      );
      const job = response?.data || response;
      if (job) {
        this.previewResult = job;
        this.fileName = job.file_name || `Job #${jobId}`;
        this.categoryId = job.rate_category_id || job.category_id || 0;
        this.categoryName = job.category_display_name || job.rate_category_name || '';

        // Detect validation failure from the wrapper response (has_errors / total_errors)
        const hasErrors = response?.has_errors === true;
        if (hasErrors) {
          this.validationFailed = true;
          this.validationFailedMessage = job.error || job.message || response?.message || 'Validation failed. The uploaded file may not match the selected category template.';
        }
        this.buildPreviewData(job);
      }
    } catch (err) {
      await AlertUtil.error('Failed to load job data. Please try again.');
      this.router.navigate([
        '/instant-rate-manager',
      ]);
    } finally {
      this.isLoading = false;
      if (this.gridApi) {
        this.gridApi.hideOverlay();
        this.autoSizeColumns();
      }
    }
  }

  /**
   * Fetch a specific page of preview rows from the backend. Inline edits made
   * on other pages are preserved via `pendingEdits` and re-applied after load.
   */
  private async loadPage(page: number): Promise<void> {
    const jobId = this.jobId || this.uploadProgress.snapshot.jobId;
    if (!jobId) return;

    this.isLoading = true;
    this.gridApi?.showLoadingOverlay();
    try {
      const response = await firstValueFrom(
        this.rateManagerService.getUploadJobStatus(jobId, {
          page,
          page_size: this.pageSize,
          rate_category_id: this.categoryId || null,
        }),
      );
      const job = response?.data || response;
      this.previewResult = job;
      this.page = page;
      this.buildPreviewData(job);
      this.applyFilter();
    } catch {
      await AlertUtil.error('Failed to load page. Please try again.');
    } finally {
      this.isLoading = false;
      this.gridApi?.hideOverlay();
      this.autoSizeColumns();
    }
  }

  async goToPage(page: number): Promise<void> {
    if (this.isLoading) return;
    const target = Math.min(Math.max(1, page), this.totalPages);
    if (target === this.page) return;
    await this.loadPage(target);
  }

  nextPage(): void {
    void this.goToPage(this.page + 1);
  }

  prevPage(): void {
    void this.goToPage(this.page - 1);
  }

  onPageSizeChange(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value);
    if (!value || value === this.pageSize) return;
    this.pageSize = value;
    void this.loadPage(1);
  }

  private buildPreviewData(apiResult: any): void {
    // The API returns row data in `rows` or `data`, and errors can be in multiple places:
    //   - apiResult.errors (standard)
    //   - apiResult.validation_errors (preview_ready / commit response)
    //   - apiResult.import_stats.errors (commit response)
    const rows: RatePreviewRow[] = [];
    const rawData: any[] = apiResult.rows || apiResult.data || [];

    // Pagination meta from the backend (falls back to a single page).
    this.page = apiResult.page ?? this.page ?? 1;
    // Don't override pageSize from the backend response — it may echo a smaller
    // slice (e.g. page_size=2 in dev). Keep the component's requested page size.
    this.totalRows = apiResult.total_rows ?? rawData.length;
    this.isPaginated = this.totalRows > rawData.length;
    // Global row offset so _rowId / _csvRow stay unique and stable across pages.
    const rowOffset = (this.page - 1) * this.pageSize;
    const errors: any[] = apiResult.validation_errors || apiResult.errors || apiResult.import_stats?.errors || apiResult.error?.validation_errors || apiResult.error?.import_stats?.errors || [];
    const warnings: any[] = apiResult.warnings || apiResult.import_stats?.warnings || [];

    // Build error/warning maps keyed by row number
    const errorsByRow = new Map<number, CellError[]>();
    for (const err of errors) {
      const rowNum = err.row;
      if (!errorsByRow.has(rowNum)) errorsByRow.set(rowNum, []);
      errorsByRow.get(rowNum)!.push({
        field: err.field || '',
        message: err.error || err.message || 'Validation error',
        severity: 'error',
      });
    }

    const warningsByRow = new Map<number, CellError[]>();
    for (const warn of warnings) {
      const rowNum = warn.row;
      if (!warningsByRow.has(rowNum)) warningsByRow.set(rowNum, []);
      warningsByRow.get(rowNum)!.push({
        field: warn.field || '',
        message: warn.message || warn.warning || 'Warning',
        severity: 'warning',
      });
    }

    // If API returned row data, use it to populate the grid
    if (rawData.length > 0) {
      // First pass: build rows with basic row number matching
      rawData.forEach((row, index) => {
        const csvRow = row.row || row._csvRow || row.row_number || rowOffset + index + 1;
        const rowErrors = errorsByRow.get(csvRow) || [];
        const rowWarnings = warningsByRow.get(csvRow) || [];

        const cellErrors: Record<string, CellError> = {};
        for (const e of [
          ...rowErrors,
          ...rowWarnings,
        ]) {
          if (e.field) {
            cellErrors[e.field] = e;
          } else {
            cellErrors['_row'] = e;
          }
        }

        const hasErrors = rowErrors.length > 0;
        const hasWarnings = rowWarnings.length > 0;
        const status = hasErrors ? 'error' : hasWarnings ? 'warning' : 'valid';

        rows.push({
          _rowId: rowOffset + index,
          _csvRow: csvRow,
          _status: status,
          _cellErrors: cellErrors,
          _excluded: false,
          _dirty: false,
          ...this.stripInternalFields(row),
        });
      });

      // Second pass: if row-number matching found no errors but we DO have errors,
      // try matching by comparing error.data content against each row's data.
      const totalMatched = rows.filter((r) => r._status === 'error').length;
      if (totalMatched === 0 && errors.length > 0) {
        this.matchErrorsByData(rows, rawData, errors);
      }

      // Set allRows before building column defs (buildColumnDefs references allRows)
      this.allRows = rows;

      // Build columns from the flattened row data
      this.buildColumnDefs(rawData[0]);
    } else {
      // No row data from API - build from error/warning row references only
      // Use a minimal placeholder approach
      this.columnDefs = [
        {
          field: '_csvRow',
          headerName: 'Row #',
          editable: false,
          maxWidth: 80,
        },
        { field: '_issue', headerName: 'Issue', editable: false, flex: 3 },
      ];

      const allRowNums = new Set([
        ...errorsByRow.keys(),
        ...warningsByRow.keys(),
      ]);
      let idx = 0;
      for (const rowNum of allRowNums) {
        const rowErrors = errorsByRow.get(rowNum) || [];
        const rowWarnings = warningsByRow.get(rowNum) || [];
        const issues = [
          ...rowErrors,
          ...rowWarnings,
        ]
          .map((e) => e.message)
          .join('; ');

        const cellErrors: Record<string, CellError> = {};
        if (issues) {
          cellErrors['_row'] = {
            field: '_row',
            message: issues,
            severity: rowErrors.length > 0 ? 'error' : 'warning',
          };
        }

        rows.push({
          _rowId: rowOffset + idx++,
          _csvRow: rowNum,
          _status: rowErrors.length > 0 ? 'error' : 'warning',
          _cellErrors: cellErrors,
          _excluded: false,
          _dirty: false,
          _issue: issues,
        });
      }
    }

    this.allRows = rows;

    // Capture backend-reported totals (across all pages) for the summary.
    this.serverErrorCount = apiResult.total_errors ?? rows.filter((r) => r._status === 'error').length;
    this.serverWarningCount = apiResult.total_warnings ?? rows.filter((r) => r._status === 'warning').length;

    // Re-apply any inline edits the user made (possibly on other pages) so
    // corrected values and cleared errors survive page navigation.
    this.applyPendingEdits();

    this.rowData = [
      ...rows,
    ];
    this.recalculateSummary();
  }

  /**
   * Re-apply tracked inline edits to the freshly-loaded page rows. Restores
   * edited values, marks rows dirty, clears fixed cell errors, and recomputes
   * each affected row's status.
   */
  private applyPendingEdits(): void {
    if (this.pendingEdits.size === 0) return;

    for (const row of this.allRows) {
      const entry = this.pendingEdits.get(row._rowId);
      if (!entry) continue;

      for (const [
        field,
        value,
      ] of Object.entries(entry.changes)) {
        row[field] = value;
        if (row._cellErrors[field]) delete row._cellErrors[field];
      }
      row._dirty = true;

      const hasErrors = Object.values(row._cellErrors).some((e) => e.severity === 'error');
      const hasWarnings = Object.values(row._cellErrors).some((e) => e.severity === 'warning');
      row._status = hasErrors ? 'error' : hasWarnings ? 'warning' : 'valid';
    }
  }

  /**
   * Fallback error matching: compare each error's `data` object against
   * the raw row data to find which grid row it belongs to.
   */
  private matchErrorsByData(gridRows: RatePreviewRow[], rawData: any[], errors: any[]): void {
    for (const err of errors) {
      const errData = err.data;
      if (!errData) continue;

      const errorMsg = err.error || err.message || 'Validation error';

      // Find the matching row by comparing field values
      const matchIndex = rawData.findIndex((raw) => {
        // Check if key fields in errData match the raw row
        const keys = Object.keys(errData);
        return keys.every((k) => String(raw[k] ?? '') === String(errData[k] ?? ''));
      });

      if (matchIndex >= 0 && gridRows[matchIndex]) {
        gridRows[matchIndex]._status = 'error';
        gridRows[matchIndex]._cellErrors['_row'] = {
          field: '_row',
          message: errorMsg,
          severity: 'error',
        };
      }
    }
  }

  /**
   * Determine which dropdown editor (if any) a column should use, based on a
   * normalized version of the field key. Handles casing and separator variants
   * such as "Origin", "origin_name", "Pickup Type", "rate unit", etc.
   */
  private getDropdownEditorType(key: string): 'location' | 'rate_unit' | 'location_type' | null {
    const n = key
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

    if (n === 'origin' || n === 'origin_name' || n === 'destination' || n === 'destination_name') {
      return 'location';
    }
    if (n === 'rate_unit' || n === 'unit') {
      return 'rate_unit';
    }
    if (n === 'pickup_type' || n === 'delivery_type') {
      return 'location_type';
    }
    return null;
  }

  private buildColumnDefs(sampleRow: any): void {
    const skipFields = new Set([
      '_rowId',
      '_csvRow',
      '_status',
      '_cellErrors',
      '_excluded',
      '_dirty',
      'row_number',
      'row',
      'id',
      'provider_id',
      'is_active',
    ]);
    const cols: ColDef[] = [
      {
        field: '_csvRow',
        headerName: '#',
        editable: false,
        maxWidth: 70,
        pinned: 'left',
        sortable: true,
        filter: false,
        cellClass: 'text-center fw-bold text-muted',
      },
    ];

    // Use the already-flattened row data keys (after stripInternalFields)
    // We get the first row from allRows which already has flattened data
    const flatSample = this.allRows.length > 0 ? this.allRows[0] : sampleRow;

    const keys = Object.keys(flatSample).filter((k) => !skipFields.has(k) && !k.startsWith('_'));

    // Columns that should not be editable (read-only, similar to rate listing)
    const readOnlyFields = new Set([
      'rate_category',
      'status',
    ]);

    for (const key of keys) {
      const normalizedKey = key
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
      const isReadOnly = readOnlyFields.has(normalizedKey);

      const colDef: ColDef = {
        field: key,
        headerName: this.formatHeaderName(key),
        editable: !isReadOnly,
        cellClass: (params: CellClassParams) => {
          const classes = this.getCellClasses(params);
          if (isReadOnly) classes.push('cell-readonly');
          return classes;
        },
      };

      // Apply dropdown editors for specific columns.
      // Uses a normalized key so it matches variants like "Origin", "origin_name",
      // "Pickup Type", etc. — not just exact snake_case field names.
      const editorType = this.getDropdownEditorType(key);
      if (editorType === 'location') {
        colDef.cellEditor = LocationCellEditorComponent;
      } else if (editorType === 'rate_unit') {
        colDef.cellEditor = DropdownCellEditorComponent;
        colDef.cellEditorParams = {
          options: [
            'per_unit',
            'per_kg',
          ],
        };
      } else if (editorType === 'location_type') {
        colDef.cellEditor = DropdownCellEditorComponent;
        colDef.cellEditorParams = {
          options: [
            'door',
            'depot',
          ],
        };
      }

      cols.push(colDef);
    }

    // Status column at the end
    cols.push({
      field: '_status',
      headerName: 'Status',
      editable: false,
      maxWidth: 110,
      pinned: 'right',
      cellRenderer: (params: any) => {
        const status = params.value;
        const classMap: Record<string, string> = {
          valid: 'badge bg-success',
          error: 'badge bg-danger',
          warning: 'badge bg-warning text-dark',
          excluded: 'badge bg-secondary',
        };
        return `<span class="${classMap[status] || 'badge bg-light text-dark'}">${status}</span>`;
      },
      tooltipValueGetter: (params: any) => {
        const rowError = params.data?._cellErrors?.['_row'];
        return rowError ? rowError.message : '';
      },
      filter: false,
    });

    this.columnDefs = cols;
  }

  private getCellClasses(params: CellClassParams): string[] {
    const classes: string[] = [];
    const field = params.colDef?.field;
    if (!field || !params.data) return classes;

    const error = params.data._cellErrors?.[field];
    if (error) {
      if (error.severity === 'error') {
        classes.push('cell-error');
      } else {
        classes.push('cell-warning');
      }
    }

    if (params.data._dirty) {
      classes.push('cell-dirty');
    }

    return classes;
  }

  private formatHeaderName(field: string): string {
    return field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  private stripInternalFields(row: any): any {
    // Remove internal tracking fields AND metadata fields that shouldn't become columns
    const skipFields = new Set([
      '_csvRow',
      '_status',
      '_cellErrors',
      '_excluded',
      '_dirty',
      '_rowId',
      'row',
      'row_number',
      'id',
      'provider_id',
      'is_active',
    ]);

    const result: any = {};
    for (const key of Object.keys(row)) {
      if (skipFields.has(key)) continue;

      const value = row[key];

      // Flatten nested objects the same way rate listing does
      if (key === 'origin' && value && typeof value === 'object') {
        result['origin_name'] = value.name ?? null;
      } else if (key === 'destination' && value && typeof value === 'object') {
        result['destination_name'] = value.name ?? null;
      } else if (key === 'rates' && value && typeof value === 'object') {
        // Flatten rates: { base_rate: { value: 100 } } → { base_rate: 100 }
        for (const rateKey of Object.keys(value)) {
          if (value[rateKey] && typeof value[rateKey] === 'object' && 'value' in value[rateKey]) {
            result[rateKey] = value[rateKey].value;
          } else {
            result[rateKey] = value[rateKey];
          }
        }
      } else if (key === 'rate_brackets' && Array.isArray(value)) {
        // Flatten rate brackets into individual columns
        for (const bracket of value) {
          const colName = this.formatBracketFieldName(bracket.range_from, bracket.range_to, bracket.unit_code);
          result[colName] = Number(bracket.bracket_rate);
        }
      } else if (key === 'rate_category' && value && typeof value === 'object') {
        result['rate_category'] = value.name ?? value;
      } else if (key === 'applied_terms' && value && typeof value === 'object') {
        result['applied_terms'] = value.name ?? value;
      } else if (key === 'group' && value && typeof value === 'object') {
        result['group'] = value.name ?? value;
      } else if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        // Generic fallback: skip nested objects that we don't know how to flatten
        // to prevent [object Object] rendering
        continue;
      } else {
        result[key] = value;
      }
    }
    return result;
  }

  private formatBracketFieldName(from: number, to: number, unitCode?: string): string {
    const safeFrom = String(from).replace(/[\.\-]/g, '_');
    const safeTo = String(to).replace(/[\.\-]/g, '_');
    const safeUnit = (unitCode || 'KG').replace(/[\.\-\s]/g, '_');
    return `bracket_${safeFrom}_${safeTo}_${safeUnit}`;
  }

  // ─── Cell Editing ─────────────────────────────────────────────────────────────

  onCellValueChanged(event: CellValueChangedEvent): void {
    const row = event.data as RatePreviewRow;
    const field = event.colDef.field;
    const newValue = event.newValue;
    const oldValue = event.oldValue;

    if (!field || newValue === oldValue) return;

    const rowId = row._rowId;
    let entry = this.pendingEdits.get(rowId);
    if (!entry) {
      entry = { changes: {}, originalValues: {}, rowData: {} };
      this.pendingEdits.set(rowId, entry);
    }

    // Track original value only the first time this field is edited
    if (!(field in entry.originalValues)) {
      entry.originalValues[field] = oldValue;
    }

    const isLocationField = this.getDropdownEditorType(field) === 'location';
    const normalizedField = field.toLowerCase();
    const isOrigin = isLocationField && normalizedField.includes('origin');
    const isDestination = isLocationField && !isOrigin;

    // If reverted back to original, remove the tracking for this field
    if (newValue === entry.originalValues[field]) {
      delete entry.changes[field];
      delete entry.originalValues[field];
      // Also remove the mapped ID field if it was a location column
      if (isOrigin) delete entry.changes['origin_id'];
      if (isDestination) delete entry.changes['destination_id'];
      if (Object.keys(entry.changes).length === 0) {
        this.pendingEdits.delete(rowId);
        row._dirty = false;
      }
    } else {
      entry.changes[field] = newValue;
      row._dirty = true;
      // For location columns, also store the selected ID
      if (isOrigin && row['origin_id']) {
        entry.changes['origin_id'] = row['origin_id'];
      }
      if (isDestination && row['destination_id']) {
        entry.changes['destination_id'] = row['destination_id'];
      }
    }

    // Clear the error for this field if the user has edited it
    if (row._cellErrors[field]) {
      delete row._cellErrors[field];

      // Recalculate row status
      const hasErrors = Object.values(row._cellErrors).some((e) => e.severity === 'error');
      const hasWarnings = Object.values(row._cellErrors).some((e) => e.severity === 'warning');
      row._status = hasErrors ? 'error' : hasWarnings ? 'warning' : 'valid';
    }

    // Keep a full snapshot of the row's committable fields so edits made on
    // this page can be committed even after navigating to other pages.
    if (this.pendingEdits.has(rowId)) {
      entry.rowData = this.cleanRowData(row);
    }

    this.gridApi.redrawRows({
      rowNodes: [
        event.node!,
      ],
    });
    this.recalculateSummary();
  }

  /** Extract a row's committable (non-internal) fields. */
  private cleanRowData(row: RatePreviewRow): Record<string, any> {
    const data: Record<string, any> = {};
    for (const key of Object.keys(row)) {
      if (!key.startsWith('_')) data[key] = row[key];
    }
    return data;
  }

  /**
   * Discard all inline edits and restore the original uploaded data.
   * Rebuilds the grid from the original preview result so values, row
   * statuses, and validation errors are restored exactly as first loaded.
   */
  async resetEdits(): Promise<void> {
    if (this.editedRowCount === 0) return;

    const result = await AlertUtil.confirm(
      'Reset edits',
      `This will discard changes to <strong>${this.editedRowCount}</strong> row(s) and restore the original uploaded data. Continue?`,
      'Yes, reset',
      'Cancel',
      'warning',
    );
    if (!result.isConfirmed) return;

    this.pendingEdits.clear();

    if (this.previewResult) {
      this.buildPreviewData(this.previewResult);
    }

    // Commit-failed errors were tied to the edited state — clear that banner.
    this.commitFailed = false;

    this.applyFilter();

    if (this.gridApi) {
      this.gridApi.refreshCells({ force: true });
      this.autoSizeColumns();
    }
  }

  // ─── Filtering ────────────────────────────────────────────────────────────────

  setFilter(filter: PreviewFilter): void {
    this.activeFilter = filter;
    this.applyFilter();
  }

  private applyFilter(): void {
    switch (this.activeFilter) {
      case 'all':
        this.rowData = [
          ...this.allRows,
        ];
        break;
      case 'valid':
        this.rowData = this.allRows.filter((r) => r._status === 'valid');
        break;
      case 'errors':
        this.rowData = this.allRows.filter((r) => r._status === 'error');
        break;
      case 'warnings':
        this.rowData = this.allRows.filter((r) => r._status === 'warning');
        break;
    }
  }

  // ─── Error Navigation ─────────────────────────────────────────────────────────

  navigateToNextError(): void {
    if (!this.gridApi) return;

    const focusedCell = this.gridApi.getFocusedCell();
    const startRowIndex = focusedCell ? focusedCell.rowIndex + 1 : 0;

    for (let i = startRowIndex; i < this.rowData.length; i++) {
      const row = this.rowData[i];
      if (row._status === 'error') {
        // Find the first error field
        const errorField = Object.keys(row._cellErrors).find((k) => row._cellErrors[k].severity === 'error');
        if (errorField) {
          this.gridApi.ensureIndexVisible(i, 'middle');
          this.gridApi.setFocusedCell(i, errorField);
          this.gridApi.startEditingCell({ rowIndex: i, colKey: errorField });
          return;
        }
      }
    }

    // Wrap around from the beginning
    for (let i = 0; i < startRowIndex; i++) {
      const row = this.rowData[i];
      if (row._status === 'error') {
        const errorField = Object.keys(row._cellErrors).find((k) => row._cellErrors[k].severity === 'error');
        if (errorField) {
          this.gridApi.ensureIndexVisible(i, 'middle');
          this.gridApi.setFocusedCell(i, errorField);
          this.gridApi.startEditingCell({ rowIndex: i, colKey: errorField });
          return;
        }
      }
    }

    AlertUtil.toast('No more errors to navigate to.', 'info', 'All clear');
  }

  // ─── Summary ──────────────────────────────────────────────────────────────────

  private recalculateSummary(): void {
    let total: number;
    let errors: number;
    let warnings: number;
    let valid: number;

    if (this.isPaginated) {
      // Server-side pagination: the current page is only a slice, so rely on
      // the totals reported by the backend. Live cell fixes are reconciled by
      // the backend re-validation on commit.
      total = this.totalRows;
      errors = this.serverErrorCount;
      warnings = this.serverWarningCount;
      valid = Math.max(0, total - errors - warnings);
    } else {
      // Single page: compute live from the loaded rows so edits update stats.
      total = this.allRows.length;
      errors = this.allRows.filter((r) => r._status === 'error').length;
      warnings = this.allRows.filter((r) => r._status === 'warning').length;
      valid = this.allRows.filter((r) => r._status === 'valid').length;
    }

    this.summary = {
      totalRows: total,
      validRows: valid,
      errorRows: errors,
      warningRows: warnings,
      excludedRows: 0,
      createdRates: this.previewResult?.created_rates || 0,
      updatedRates: this.previewResult?.updated_rates || 0,
      skippedRoutes: this.previewResult?.skipped_routes || 0,
    };

    this.selectedRowCount = this.gridApi?.getSelectedRows()?.length || 0;
  }

  // ─── Export CSV ───────────────────────────────────────────────────────────────

  exportCsv(): void {
    if (!this.gridApi) return;
    this.gridApi.exportDataAsCsv({
      fileName: `rates_preview_${this.fileName}`,
      columnKeys: this.columnDefs.filter((c) => c.field && !c.field.startsWith('_')).map((c) => c.field!),
    });
  }

  // ─── Submit / Commit ────────────────────────────────────────────────────────

  async commitChanges(): Promise<void> {
    const errorCount = this.summary.errorRows;
    const editedCount = this.editedRowCount;
    const editedNote = editedCount > 0 ? `<br><strong>${editedCount}</strong> edited row(s) will be submitted with your changes.` : '';

    // Error rows don't block the commit — the backend skips them
    // (skip_errors: true in the payload). We just confirm what will happen.
    const result =
      errorCount > 0
        ? await AlertUtil.confirm(
            'Commit valid rows',
            `<strong>${errorCount}</strong> row(s) with errors will be skipped. Only <strong>${this.summary.validRows}</strong> valid rate(s) will be committed.${editedNote} Continue?`,
            'Yes, commit valid rows',
            'Cancel',
            'warning',
          )
        : await AlertUtil.confirm(
            'Commit Rates',
            `Are you sure you want to commit <strong>${this.summary.validRows}</strong> valid rate(s) to the database? This action cannot be undone.${editedNote}`,
            'Yes, commit',
            'Cancel',
          );

    if (!result.isConfirmed) return;

    await this.performCommit();
  }

  async commitWithErrors(): Promise<void> {
    // Confirmation for proceeding with errors
    const result = await AlertUtil.confirm(
      'Commit with errors',
      `<strong>${this.summary.errorRows}</strong> row(s) with errors will be skipped. Only <strong>${this.summary.validRows}</strong> valid rate(s) will be committed. Continue?`,
      'Yes, commit anyway',
      'Cancel',
      'warning',
    );
    if (!result.isConfirmed) return;

    await this.performCommit(true);
  }

  private async performCommit(forceWithErrors = false): Promise<void> {
    // Get the job ID from query param, component state, or upload progress state
    const jobId = this.jobId || this.uploadProgress.snapshot.jobId;
    if (!jobId) {
      await AlertUtil.error('No upload job found. Please re-upload your file.');
      return;
    }

    this.isSubmitting = true;

    try {
      // Build the commit payload with only edited rows
      const commitPayload = this.buildCommitPayload();

      // Commit the validated job to the database
      // POST /upload-jobs/{jobId}/commit
      await this.uploadProgress.start({
        mode: 'commit',
        fileName: this.fileName,
        request: () => this.rateManagerService.commitUploadJob(jobId, commitPayload),
      });

      this.commitFailed = false;
      this.router.navigate([
        '/instant-rate-manager/rates',
      ]);
    } catch (err: any) {
      // Handle commit validation errors — show them in the grid
      const errorBody = err?.error?.error || err?.error || {};
      const validationErrors: any[] = errorBody.validation_errors || [];

      if (validationErrors.length > 0) {
        this.applyCommitErrors(validationErrors);
        this.commitFailed = true;
      } else {
        // For non-validation errors, still allow retry
        this.commitFailed = true;
      }
      // Other errors are handled by the progress service toast
    } finally {
      this.isSubmitting = false;
    }
  }

  /**
   * Build the commit payload. Only includes edited rows.
   * Payload structure: { rows, columns, create_zone, skip_errors }
   */
  private buildCommitPayload(): {
    rows: Record<string, any>[];
    columns: string[];
    create_zone: boolean;
    skip_errors: boolean;
  } {
    // Collect all edited rows from every page. Each pendingEdits entry keeps a
    // full snapshot of the row's committable fields, so edits made on pages
    // that are no longer loaded are still included.
    const editedRows: Record<string, any>[] = Array.from(this.pendingEdits.values()).map((entry) => entry.rowData);

    // Extract column names from the row data
    const columns: string[] = editedRows.length > 0 ? Object.keys(editedRows[0]) : this.columnDefs.filter((c) => c.field && !c.field.startsWith('_')).map((c) => c.field!);

    return {
      rows: editedRows,
      columns,
      create_zone: true,
      skip_errors: true,
    };
  }

  /**
   * Apply commit validation errors back into the AG Grid rows.
   * Each error has { row, error, data } — we match by row number and mark the row.
   */
  private applyCommitErrors(validationErrors: any[]): void {
    // Build a map of row number -> error message
    const errorsByRow = new Map<number, string>();
    for (const ve of validationErrors) {
      errorsByRow.set(ve.row, ve.error || 'Validation error');
    }

    // Apply errors to matching rows
    for (const row of this.allRows) {
      const errorMsg = errorsByRow.get(row._csvRow);
      if (errorMsg) {
        row._status = 'error';
        // Add a general row-level error (not field-specific since commit errors are row-level)
        row._cellErrors['_row'] = {
          field: '_row',
          message: errorMsg,
          severity: 'error',
        };
      }
    }

    this.recalculateSummary();
    this.applyFilter();

    if (this.gridApi) {
      this.gridApi.refreshCells({ force: true });
    }
  }

  async goBack(): Promise<void> {
    const result = await AlertUtil.confirm(
      'Discard preview and re-upload?',
      'This will discard the current preview and allow you to upload a new file. The existing preview data will be removed.',
      'Discard',
      'Cancel',
    );

    if (!result.isConfirmed) return;

    // Capture category context before clearing state
    const categoryId = this.categoryId;
    const categoryName = this.categoryName;

    // Delete the job from the backend if we have a jobId
    const jobId = this.jobId || this.uploadProgress.snapshot.jobId;
    if (jobId) {
      try {
        await firstValueFrom(this.rateManagerService.deleteUploadJob(jobId));
      } catch {
        // Continue regardless
      }
    }

    this.uploadProgress.dismiss();

    // Navigate back to listings, then re-open the bulk upload modal
    // with the category pre-selected so the user skips to file selection.
    this.router
      .navigate([
        '/instant-rate-manager',
      ])
      .then(() => {
        if (categoryId && categoryName) {
          const modalRef = this.modalService.open(BulkCreateRateModalComponent, {
            size: 'lg',
            centered: true,
          });
          modalRef.componentInstance.preSelectedCategory = {
            id: categoryId,
            name: categoryName,
          };
        }
      });
  }

  onSelectionChanged(): void {
    this.selectedRowCount = this.gridApi?.getSelectedRows()?.length || 0;
  }
}
